import { z } from "zod";

export const BrowserTab = z.object({ id: z.string(), url: z.string(), title: z.string(), agentActive: z.boolean() });
export type BrowserTab = z.infer<typeof BrowserTab>;

export const BrowserStatus = z.enum(["stopped", "starting", "ready", "error"]);
export type BrowserStatus = z.infer<typeof BrowserStatus>;

export const BrowserState = z.object({
  status: BrowserStatus,
  error: z.string().nullable(),
  version: z.string().nullable(),
  tabs: z.array(BrowserTab),
  activeTabId: z.string().nullable(),
  /** true while a human holds control; agent tools refuse to act. */
  humanControl: z.boolean(),
  headed: z.boolean(),
  viewers: z.number(),
  lastAgentAction: z.object({ tabId: z.string().nullable(), action: z.string(), at: z.string() }).nullable(),
});
export type BrowserState = z.infer<typeof BrowserState>;

export const DevicePreset = z.enum(["desktop", "tablet", "mobile"]);
export type DevicePreset = z.infer<typeof DevicePreset>;
export const DEVICE_SIZES: Record<DevicePreset, { width: number; height: number; mobile: boolean }> = {
  desktop: { width: 1280, height: 800, mobile: false },
  tablet: { width: 768, height: 1024, mobile: false },
  mobile: { width: 390, height: 844, mobile: true },
};

/** Element picker capture, injected into the composer as a [Browser element] block. */
export const BrowserElementCapture = z.object({
  url: z.string(),
  title: z.string(),
  selector: z.string(),
  tag: z.string(),
  id: z.string().nullable(),
  classes: z.array(z.string()),
  role: z.string().nullable(),
  ariaLabel: z.string().nullable(),
  text: z.string(),
  html: z.string(),
  rect: z.object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() }),
  viewport: z.object({ width: z.number(), height: z.number() }),
  styles: z.record(z.string(), z.string()),
  parents: z.array(z.string()),
});
export type BrowserElementCapture = z.infer<typeof BrowserElementCapture>;

export function formatElementCapture(c: BrowserElementCapture): string {
  const lines = [
    "[Browser element]",
    `URL: ${c.url}`,
    `Title: ${c.title}`,
    `Selector: ${c.selector}`,
    `Tag: ${c.tag}${c.id ? `  ID: ${c.id}` : ""}${c.classes.length ? `  Classes: ${c.classes.join(" ")}` : ""}`,
    ...(c.role || c.ariaLabel ? [`Role: ${c.role ?? "-"}  ARIA label: ${c.ariaLabel ?? "-"}`] : []),
    `Box: x=${Math.round(c.rect.x)} y=${Math.round(c.rect.y)} w=${Math.round(c.rect.width)} h=${Math.round(c.rect.height)}  Viewport: ${c.viewport.width}x${c.viewport.height}`,
    ...(c.parents.length ? [`Parents: ${c.parents.join(" > ")}`] : []),
    "Styles:",
    ...Object.entries(c.styles).map(([k, v]) => `- ${k}: ${v}`),
    ...(c.text ? [`Text: ${c.text}`] : []),
    `HTML: ${c.html}`,
    "[/Browser element]",
  ];
  return lines.join("\n");
}

/** Client -> server frames on /ws/browser/:projectId */
export const BrowserClientMessage = z.discriminatedUnion("type", [
  z.object({ type: z.literal("attach"), tabId: z.string().nullable(), width: z.number().int(), height: z.number().int(), scale: z.number().min(0.25).max(2).default(1) }),
  z.object({ type: z.literal("resize"), width: z.number().int(), height: z.number().int(), scale: z.number().min(0.25).max(2).default(1) }),
  z.object({ type: z.literal("navigate"), url: z.string() }),
  z.object({ type: z.literal("history"), direction: z.enum(["back", "forward"]) }),
  z.object({ type: z.literal("reload") }),
  z.object({ type: z.literal("stop_loading") }),
  z.object({ type: z.literal("tabs.create"), url: z.string().optional() }),
  z.object({ type: z.literal("tabs.close"), tabId: z.string() }),
  z.object({ type: z.literal("tabs.select"), tabId: z.string() }),
  z.object({ type: z.literal("device"), preset: DevicePreset }),
  z.object({ type: z.literal("control"), human: z.boolean() }),
  z.object({ type: z.literal("mode"), headed: z.boolean() }),
  z.object({ type: z.literal("picker"), enabled: z.boolean() }),
  z.object({
    type: z.literal("mouse"),
    event: z.enum(["move", "down", "up", "wheel"]),
    x: z.number(),
    y: z.number(),
    button: z.enum(["left", "right", "middle", "none"]).default("left"),
    clickCount: z.number().int().default(1),
    deltaX: z.number().default(0),
    deltaY: z.number().default(0),
    modifiers: z.number().int().default(0),
  }),
  z.object({ type: z.literal("key"), event: z.enum(["down", "up"]), key: z.string(), code: z.string(), keyCode: z.number().int(), text: z.string().optional(), modifiers: z.number().int().default(0) }),
  z.object({ type: z.literal("text"), text: z.string() }),
  z.object({ type: z.literal("frame_ack"), sessionId: z.number().int() }),
  z.object({ type: z.literal("stop") }),
]);
export type BrowserClientMessage = z.infer<typeof BrowserClientMessage>;

/** Server -> client. Frames are sent as binary (JPEG) prefixed by a 1-byte tag; everything else JSON. */
export const BrowserServerMessage = z.discriminatedUnion("type", [
  z.object({ type: z.literal("state"), state: BrowserState }),
  z.object({ type: z.literal("tab"), tabId: z.string(), url: z.string(), title: z.string(), loading: z.boolean(), canGoBack: z.boolean(), canGoForward: z.boolean() }),
  z.object({ type: z.literal("frame_meta"), tabId: z.string(), width: z.number(), height: z.number(), scale: z.number() }),
  z.object({ type: z.literal("console"), tabId: z.string(), level: z.string(), text: z.string(), at: z.number() }),
  z.object({ type: z.literal("network"), tabId: z.string(), id: z.string(), method: z.string(), url: z.string(), status: z.number().nullable(), resourceType: z.string().nullable() }),
  z.object({ type: z.literal("picker_hover"), capture: BrowserElementCapture.nullable() }),
  z.object({ type: z.literal("picker_selected"), capture: BrowserElementCapture }),
  z.object({ type: z.literal("download"), fileName: z.string(), state: z.string() }),
  z.object({ type: z.literal("error"), code: z.string(), message: z.string().optional() }),
]);
export type BrowserServerMessage = z.infer<typeof BrowserServerMessage>;

// ---------------- app previews ----------------

export const ProjectApp = z.object({
  port: z.number().int(),
  loopback: z.boolean(),
  pid: z.number().int().nullable(),
  process: z.string(),
  /** URL for the user's own browser (subdomain through the Codvero preview proxy). */
  previewUrl: z.string(),
  /** URL for the in-container Codvero browser (same network namespace). */
  internalUrl: z.string(),
});
export type ProjectApp = z.infer<typeof ProjectApp>;

export const PreviewEntry = z.object({ url: z.string(), expiresAt: z.string() });
export type PreviewEntry = z.infer<typeof PreviewEntry>;

export const PreviewShare = z.object({ id: z.string(), port: z.number().int(), url: z.string().nullable(), createdAt: z.string() });
export type PreviewShare = z.infer<typeof PreviewShare>;
export const CreateShareInput = z.object({ port: z.number().int().min(1024).max(65535) });

export const SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
export const RESERVED_SUBDOMAINS = ["admin", "api", "app", "apps", "assets", "auth", "blog", "cdn", "code", "codvero", "dev", "docs", "ftp", "help", "login", "mail", "mcp", "remote", "smtp", "static", "status", "support", "www"];
export const PublicDeployment = z.object({ subdomain: z.string(), port: z.number().int(), url: z.string(), createdAt: z.string(), updatedAt: z.string() });
export type PublicDeployment = z.infer<typeof PublicDeployment>;
export const PublishInput = z.object({ subdomain: z.string().regex(SUBDOMAIN_RE), port: z.number().int().min(1024).max(65535) });
export const RenameDeploymentInput = z.object({ subdomain: z.string().regex(SUBDOMAIN_RE) });
export const DeploymentEvent = z.object({
  id: z.string(),
  action: z.enum(["publish", "update", "unpublish", "rollback"]),
  subdomain: z.string(),
  port: z.number(),
  previous: z.string().nullable(),
  url: z.string(),
  /** this event's subdomain is live on this port right now */
  current: z.boolean(),
  createdAt: z.string(),
});
export type DeploymentEvent = z.infer<typeof DeploymentEvent>;
/** The domain public apps are served under (e.g. codvero.com), with the history of this project's domains. */
export const DeploymentHistory = z.object({ domain: z.string(), scheme: z.string(), events: z.array(DeploymentEvent) });
export type DeploymentHistory = z.infer<typeof DeploymentHistory>;

// ---------------- files + git ----------------

export const FileEntry = z.object({ name: z.string(), type: z.enum(["dir", "file", "link"]), size: z.number().nullable(), mtime: z.number().nullable(), hidden: z.boolean() });
export type FileEntry = z.infer<typeof FileEntry>;
export const DirListing = z.object({ path: z.string(), entries: z.array(FileEntry) });
export type DirListing = z.infer<typeof DirListing>;
export const FileContent = z.object({ path: z.string(), size: z.number(), truncated: z.boolean(), binary: z.boolean(), mime: z.string(), content: z.string().nullable() });
export type FileContent = z.infer<typeof FileContent>;
export const FileSearch = z.object({ query: z.string(), hits: z.array(z.object({ path: z.string(), type: z.enum(["dir", "file"]) })), visited: z.number(), capped: z.boolean() });
export type FileSearch = z.infer<typeof FileSearch>;

export const GitRepo = z.object({ path: z.string(), branch: z.string().nullable(), dirty: z.boolean(), ahead: z.number().nullable() });
export type GitRepo = z.infer<typeof GitRepo>;
export const GitCommit = z.object({ sha: z.string(), short: z.string(), subject: z.string(), author: z.string(), date: z.string(), current: z.boolean() });
export type GitCommit = z.infer<typeof GitCommit>;
export const GitDiffFile = z.object({ path: z.string(), status: z.string(), additions: z.number(), deletions: z.number(), patch: z.string(), truncated: z.boolean() });
export const GitCommitDetail = z.object({ commit: GitCommit, body: z.string(), files: z.array(GitDiffFile) });
export type GitCommitDetail = z.infer<typeof GitCommitDetail>;
export const GitCheckoutInput = z.object({ repo: z.string(), sha: z.string().regex(/^[0-9a-f]{7,40}$/), checkpointMessage: z.string().max(200).optional() });
export const GitInitInput = z.object({ path: z.string().default(""), message: z.string().max(200).default("Initial checkpoint") });
export const GitCheckpointInput = z.object({ repo: z.string(), message: z.string().trim().min(1).max(200) });

// ---------------- notes, memory, schedules ----------------

export const ProjectNote = z.object({ content: z.string(), updatedAt: z.string().nullable() });
export const SaveNoteInput = z.object({ content: z.string().max(524_288) });
export const ProjectMemory = z.object({ content: z.string(), exists: z.boolean(), path: z.string(), memoryDefault: z.boolean() });
export const SaveMemoryInput = z.object({ content: z.string().max(2_000_000) });
/** One memory file as the Chat section shows it. */
export interface MemoryDoc {
  path: string;
  content: string;
  exists: boolean;
}
/** A Chat section conversation's memories: its own (or the one it shares) and its space's. */
export interface ChatMemory {
  useMemory: boolean;
  /** the user's own memory, read by all of their conversations */
  user: MemoryDoc;
  own: MemoryDoc & { sharedWith: { id: string; title: string } | null };
  space: (MemoryDoc & { id: string; name: string }) | null;
}
export const SaveChatMemoryInput = SaveMemoryInput.extend({ scope: z.enum(["own", "space", "user"]) });

export const ScheduleKind = z.enum(["once", "cron"]);
export const ScheduleStatus = z.enum(["paused", "active", "done", "error"]);
export const Schedule = z.object({
  id: z.string(),
  projectId: z.string(),
  chatId: z.string().nullable(),
  name: z.string(),
  prompt: z.string(),
  kind: ScheduleKind,
  cron: z.string().nullable(),
  runAt: z.string().nullable(),
  timezone: z.string(),
  maxRuns: z.number().nullable(),
  runs: z.number(),
  status: ScheduleStatus,
  nextRunAt: z.string().nullable(),
  lastRunAt: z.string().nullable(),
  lastResult: z.string().nullable(),
  /** Chat section tasks: the providers its conversation asks */
  accountIds: z.array(z.string()).nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
});
export type Schedule = z.infer<typeof Schedule>;
export const CreateScheduleInput = z.object({
  name: z.string().trim().min(1).max(120),
  prompt: z.string().trim().min(1).max(20_000),
  kind: ScheduleKind,
  cron: z.string().trim().max(100).optional(),
  runAt: z.string().datetime({ offset: true }).optional(),
  timezone: z.string().max(64).default("UTC"),
  maxRuns: z.number().int().min(1).max(10_000).optional(),
  chatId: z.string().optional(),
  /** Chat section: the providers (accounts) the task's conversation asks (default: the default provider). */
  accountIds: z.array(z.string()).min(1).max(8).optional(),
  /** Arm immediately (default: created paused, like the original's human review). */
  active: z.boolean().default(false),
});
export type CreateScheduleInput = z.infer<typeof CreateScheduleInput>;
export const UpdateScheduleInput = CreateScheduleInput.partial();
